/* tslint:disable:max-line-length */
import { Avatar } from '@mm/components/src';
import { selectActiveFacebookPage } from '@mm/state/selectors/facebookPageSelector';
import { PlatformType } from '@mm/state/model/CampaignsModel';
import WidgetModel, {
  DialogueWidget,
  WidgetTypes,
} from '@mm/state/model/WidgetModel';
import React, { useEffect, useMemo, useState } from 'react';
import { getUniqId } from '@mm/utils';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import noImage from 'assets/icons/contact_noimage.svg';
import './DialoguePreview.pcss';
import {
  isUserMessage,
  useInteractivePreview,
} from './use-interactive-preview';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../common/widgets/common/PathGraph/PathGraph';
import { WidgetPreview } from './WidgetPreview';

type Props = {
  className?: string;
  dialogueId: number;
  sticky?: boolean;
  top?: number;
  style?: React.HTMLAttributes<HTMLDivElement>['style'];
  platforms?: PlatformType[];
  isSms?: boolean;
  customAvailableWidgets?: WidgetTypes[];
  setWidgetCount?: (v: number) => void;
};

const transformWidgetsBeforeDisplay = (
  widgetsArray: DialogueWidget[],
  isSms?: boolean,
) => {
  if (isSms) {
    return widgetsArray.map(widget => {
      if (!isUserMessage(widget)) {
        const newWidget = widget as WidgetModel;
        return {
          ...newWidget,
          config: {
            ...newWidget.config,
            body: newWidget.smsStructure?.body || newWidget.config.body,
          },
        } as WidgetModel;
      }
      return widget;
    });
  }
  return widgetsArray;
};

const DialoguePreviewComponent = ({
  dialogueId,
  className,
  sticky,
  style,
  platforms,
  isSms,
  customAvailableWidgets,
  setWidgetCount,
}: Props) => {
  const facebookPage = useSelector(selectActiveFacebookPage);
  const src = facebookPage ? facebookPage.squarePicture : noImage;
  const username = facebookPage?.name || 'Preview';
  const pathGraph = useNewGraphInstance(dialogueId);
  const { previewList, setPreviewToStore, setActiveWidgetIndex } =
    useInteractivePreview({
      dialogueId,
      platforms,
      customAvailableWidgets,
      setWidgetCount,
    });
  const [localPreview, setLocalPreview] = useState(previewList);

  const cn = useMemo(
    () => ({
      container: classNames(className, 'dp-container', {
        'dp-container--sticky': sticky,
      }),
    }),
    [className],
  );

  useEffect(() => {
    const newPreview = transformWidgetsBeforeDisplay(previewList, isSms);
    setLocalPreview(newPreview);
  }, [previewList]);

  return (
    <PathGraphContext.Provider value={pathGraph}>
      <div className={cn.container} style={style}>
        <div className="dp-header">
          <Avatar className="dp-avatar" src={src} />
          <div className="dp-username">{username}</div>
        </div>

        <div className="dp-conversation">
          {localPreview.map((widget, index) => {
            if (widget.type === 'user_message') {
              return (
                <div
                  className="dp-user-message-container"
                  key={getUniqId(`${widget.id}_${index}`)}
                >
                  <div className="dp-user-message">{widget.message}</div>
                </div>
              );
            }
            return (
              <WidgetPreview
                key={getUniqId(`${widget.id}_${index}`)}
                isSms={isSms}
                platforms={platforms}
                widget={widget as WidgetModel}
                avatarSrc={src}
                currentDialogueId={(previewList[0] as WidgetModel)?.pageId}
                setPreviewToStore={setPreviewToStore}
                setActiveWidgetIndex={() =>
                  setActiveWidgetIndex(
                    (widget as WidgetModel).metadata?.previewIndex ?? 0,
                  )
                }
              />
            );
          })}
        </div>

        <div className="dp-reply">
          <div className="dp-icon">
            <svg viewBox="0 0 24 24" height="20px" width="20px">
              <g fillRule="evenodd">
                <polygon fill="none" points="-6,30 30,30 30,-6 -6,-6 " />
                <path
                  d="m18,11l-5,0l0,-5c0,-0.552 -0.448,-1 -1,-1c-0.5525,0 -1,0.448 -1,1l0,5l-5,0c-0.5525,0 -1,0.448 -1,1c0,0.552 0.4475,1 1,1l5,0l0,5c0,0.552 0.4475,1 1,1c0.552,0 1,-0.448 1,-1l0,-5l5,0c0.552,0 1,-0.448 1,-1c0,-0.552 -0.448,-1 -1,-1m-6,13c-6.6275,0 -12,-5.3725 -12,-12c0,-6.6275 5.3725,-12 12,-12c6.627,0 12,5.3725 12,12c0,6.6275 -5.373,12 -12,12"
                  fill="#C1C1C1"
                />
              </g>
            </svg>
          </div>

          <div className="dp-input">
            <span>Send message to {username}</span>

            <div className="dp-icon">
              <svg height="20px" viewBox="0 0 38 38" width="20px">
                <g fill="none" fillRule="evenodd">
                  <g transform="translate(-893.000000, -701.000000)">
                    <g transform="translate(709.000000, 314.000000)">
                      <g>
                        <path
                          d="M210.5,405 C209.121,405 208,403.879 208,402.5 C208,401.121 209.121,400 210.5,400 C211.879,400 213,401.121 213,402.5 C213,403.879 211.879,405 210.5,405 M212.572,411.549 C210.428,413.742 206.938,415 203,415 C199.062,415 195.572,413.742 193.428,411.549 C192.849,410.956 192.859,410.007 193.451,409.428 C194.045,408.85 194.993,408.859 195.572,409.451 C197.133,411.047 199.909,412 203,412 C206.091,412 208.867,411.047 210.428,409.451 C211.007,408.859 211.956,408.85 212.549,409.428 C213.141,410.007 213.151,410.956 212.572,411.549 M195.5,400 C196.879,400 198,401.121 198,402.5 C198,403.879 196.879,405 195.5,405 C194.121,405 193,403.879 193,402.5 C193,401.121 194.121,400 195.5,400 M203,387 C192.523,387 184,395.523 184,406 C184,416.477 192.523,425 203,425 C213.477,425 222,416.477 222,406 C222,395.523 213.477,387 203,387"
                          fill="#C1C1C1"
                        />
                      </g>
                    </g>
                  </g>
                </g>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </PathGraphContext.Provider>
  );
};

export const DialoguePreview = React.memo(
  DialoguePreviewComponent,
  (prevProps, props) => {
    return (
      prevProps.dialogueId === props.dialogueId &&
      isEqual(prevProps.platforms, props.platforms)
    );
  },
);
